import { Component, OnInit } from '@angular/core';
import {
    BaseComponent,
    ErrorsUtil,
    MessageBoxComponent,
    MessageContent,
    WaitComponent
} from '@sircon/sircon-ui-framework';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, concat, finalize, Observable, of, takeUntil, tap } from 'rxjs';
import { CarrierInformation } from 'src/app/core/models/carrier-information.model';
import { ProducerEdgeHttpService } from 'src/providers/producer-edge-http.service';
import { ProducerManagerIntegrationHttpService } from 'src/providers/producer-manager-integration-http.service';
import { UiLoggingHttpService } from 'src/providers/ui-logging-http.service';
import { AuthenticationService } from 'src/services/authentication.service';
import { OneTimeAlertService } from 'src/services/one-time-alert.service';
import { UserContextService } from 'src/services/user-context.service';

@Component({
    selector: 'app-sso-login',
    templateUrl: './sso-login.component.html',
    imports: [WaitComponent, MessageBoxComponent]
})
export class SsoLoginComponent extends BaseComponent implements OnInit {
    errors: MessageContent = ErrorsUtil.create();
    redirectRoute: string;
    redirectParam: Array<string>;
    samlConfirmationId: string;
    subscriberId: string;
    loading = true;

    // Angular Router properties
    token: string;
    entityType: string;
    brandKey: string;
    userName: string;
    agencySubscriberId: string;

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private authenticationService: AuthenticationService,
        private uiLoggingHttpService: UiLoggingHttpService,
        private oneTimeAlertService: OneTimeAlertService,
        private producerManagerIntegrationHttpService: ProducerManagerIntegrationHttpService,
        private userContextService: UserContextService,
        private producerEdgeHttpService: ProducerEdgeHttpService
    ) {
        super();
    }

    ngOnInit(): void {
        this.initializePropertiesFromAngularRouter();
        if (!this.token) {
            // authenticationService.isAuthenticated returns true for an undefined cookie value.
            // It seems to be parsing it as an empty string, even though devtools does not.
            if (!this.authenticationService.isAuthenticated() || this.authenticationService.getJwtToken()?.length === 0) {
                const loginRoute = this.entityType === 'firm' ? '/agencyLogin' : '/login';
                const subscriberIdForLogin = this.entityType === 'firm' ?
                    this.agencySubscriberId :
                    this.subscriberId;

                this.router.navigate([loginRoute], {
                    queryParams: {
                        brandKey: this.brandKey,
                        userName: this.userName,
                        subscriberId: subscriberIdForLogin
                    }
                });
                return;
            }
        }
        if (this.samlConfirmationId &&
            this.subscriberId &&
            !this.authenticationService.isRestrictedToOneCarrier()) {
            this.confirmSamlIdAndRedirect(this.samlConfirmationId, this.subscriberId);
        } else {
            this.setCookieAndLoadUserContext(this.token);
        }
    }

    setCookieAndLoadUserContext(token: string): void {
        this.authenticationService.authenticateByTokenAsObservable(token)
            .pipe(
                tap(() => {
                    this.completeAuthentication();
                }),
                catchError((response) => {
                    ErrorsUtil.mapResponse(response);
                    return of({});
                }),
                finalize(() => {
                    this.loading = false;
                })).subscribe();
    }

    initializePropertiesFromAngularRouter(): void {
        this.activatedRoute.queryParamMap.pipe(takeUntil(this.ngUnsubscribe)).subscribe(params => {
            this.token = params.get('token');
            this.entityType = params.get('entityType');
            this.brandKey = params.get('brandKey');
            this.userName = params.get('userName');
            this.subscriberId = params.get('subscriberId');
            this.agencySubscriberId = params.get('agencySubscriberId');
            this.redirectRoute = params.get('redirectRoute');
            this.samlConfirmationId = params.get('samlConfirmationId');

            // redirectParam may arrive either as repeated keys (?redirectParam=a=1&redirectParam=b=2,
            // as sent by external/PM links) or as a single comma-separated value (as some in-app
            // callers send it). getAll() captures every occurrence; splitting each on comma covers both.
            const redirectParamValues = params.getAll('redirectParam');
            this.redirectParam = redirectParamValues.length
                ? redirectParamValues.flatMap(value => value.split(','))
                : undefined;
        });
    }

    completeAuthentication(): void {
        this.oneTimeAlertService.sendAllOneTimeAlerts()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe();

        if (this.redirectRoute) {
            const redirectRouteParams: any = {};
            if (this.redirectParam) {
                this.redirectParam.forEach((param) => {
                    const values = param.split('=');
                    redirectRouteParams[values[0]] = values[1];
                });
            }
            // Ignoring errors because this is just logging.
            this.uiLoggingHttpService.log({
                level: 'info',
                message: `ssoLogin redirect to route ${this.redirectRoute}` +
                    ` with redirectRouteParams ${JSON.stringify(redirectRouteParams)} samlConfirmationId: ${this.samlConfirmationId}`
            })
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe();

            // Convert UI-Router route names to Angular Router paths
            const angularRouterPath = this.convertUiRouterToAngularRoute(this.redirectRoute);
            this.router.navigate([angularRouterPath], {
                queryParams: redirectRouteParams
            });
        } else {
            this.router.navigate(['/dashboard']);
        }
    }

    confirmSamlIdAndRedirect(samlConfirmationId: string, subscriberId: string): void {
        concat(this.producerManagerIntegrationHttpService.activateSamlConfirmationId(samlConfirmationId),
            this.redirectToCarrierProfile(subscriberId))
            .pipe(
                catchError((response) => {
                    if (response.status === 404) {
                        // edge-service maps 403 to a 404. While this may not actually be a 403, we will treat it as such for this particular endpoint
                        const forbiddenError = ErrorsUtil.create();
                        forbiddenError.level = 'error';
                        forbiddenError.validationError = true;
                        // The login/agencyLogin pages read this param via JSON.parse, so serialize the
                        // errors object. Angular Router would otherwise stringify it to "[object Object]",
                        // which then throws in JSON.parse on the receiving page.
                        this.redirectToLogin({
                            errors: JSON.stringify(forbiddenError)
                        });
                    } else {
                        this.redirectToLogin({
                            errors: JSON.stringify(ErrorsUtil.mapResponse(response))
                        });
                    }
                    return of({});
                }),
                finalize(() => {
                    this.loading = false;
                }),
                takeUntil(this.ngUnsubscribe)
            ).subscribe();
    }

    redirectToLogin(extraParams?: object): void {
        if (this.entityType === 'firm') {
            this.router.navigate(['/agencyLogin'], {
                queryParams: {
                    redirectRoute: 'ssoLogin',
                    redirectParam: `samlConfirmationId=${this.samlConfirmationId},subscriberId=${this.subscriberId}`,
                    brandKey: this.brandKey,
                    // This subscriber id is the agency account id
                    subscriberId: this.agencySubscriberId,
                    ...extraParams
                }
            });
        } else {
            this.router.navigate(['/login'], {
                queryParams: {
                    samlConfirmationId: this.samlConfirmationId,
                    subscriberId: this.subscriberId,
                    brandKey: this.brandKey,
                    userName: this.userName,
                    ...extraParams
                }
            });
        }
    }

    redirectToCarrierProfile(subscriberId: string): Observable<CarrierInformation> {
        return this.producerEdgeHttpService.getCarrierBySubscriberId(subscriberId).pipe(
            tap(carrierInformation => {
                if (this.userContextService.isAgencyCapabilitiesAccount()) {
                    this.router.navigate(['/network/carrierAgencyRedirect'], {
                        queryParams: {
                            carrierNetworkId: carrierInformation.carrierNetworkId,
                            carrierName: carrierInformation.carrierName
                        }
                    });
                } else {
                    this.router.navigate(['/dashboard/carrierProfile/activity'], {
                        queryParams: { carrierNetworkId: carrierInformation.carrierNetworkId }
                    });
                }
            })
        );
    }

    /**
     * Convert UI-Router state names to Angular Router paths
     */
    private convertUiRouterToAngularRoute(uiRouterState: string): string {
        const routeMapping: { [key: string]: string } = {
            'dashboard': '/dashboard',
            'dashboard.activity': '/dashboard/activity',
            'dashboard.allActions': '/dashboard/allActions',
            'dashboard.carrierProfile.activity': '/dashboard/carrierProfile/activity',
            'network.carrierAgencyRedirect': '/network/carrierAgencyRedirect',
            'inviteSignup.processInvite': '/inviteSignup/processInvite',
            'ssoLogin': '/ssoLogin',
            'login': '/login'
        };

        return routeMapping[uiRouterState] || `/${uiRouterState}`;
    }
}
